import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { deleteWorkflowItemMutationDocument } from '@liscio/api';
import { useGraphQLClient } from '@liscio/common';

/**
 * Hook for deleting a custom workflow item from a request.
 *
 * Only works when the associated request is in draft status, enforced
 * by the backend `DeleteWorkflowItem` use case.
 */
export function useDeleteItem() {
  const gqlClient = useGraphQLClient();
  const queryClient = useQueryClient();

  const { mutateAsync: deleteMutation, isLoading: isDeleting } =
    gqlClient.useMutationGraphQL(deleteWorkflowItemMutationDocument, {
      onError: () => {
        toast.error('Failed to remove item.');
      },
    });

  const deleteItem = useCallback(
    async (workflowItemId: string) => {
      try {
        const response = await deleteMutation({ workflowItemId });

        if (response.deleteWorkflowItem?.errors?.length) {
          const errMsg = response.deleteWorkflowItem.errors[0].message;
          toast.error(`Could not remove item: ${errMsg}`);
          return false;
        }

        toast.success('Item removed successfully.');

        // Invalidate caches so the UI refetches updated sections and items
        queryClient.invalidateQueries(['workflowRequest']);
        queryClient.invalidateQueries(['items']);
        queryClient.invalidateQueries(['/api/v5/firms/requests']);

        return true;
      } catch {
        return false;
      }
    },
    [deleteMutation, queryClient]
  );

  return { deleteItem, isDeleting };
}
